import React from 'react';
import { useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const WelcomePage = ({ onStartStory, buttonText, buttonClasses, showPaymentStatus = false, paymentMessage = '' }) => {
  const { language } = useLanguage();

  const [userName, setUserName] = useState('');

  useEffect(() => {
    const updateUserName = () => {
      // Get family name from localStorage (set in ProfilePage)
      const familyName = localStorage.getItem('familyName');
      
      // Use family name if it exists and is not empty
      if (familyName && familyName.trim() !== '') {
        setUserName(familyName);
      } else {
        // Don't display anything if no family name is set
        setUserName('');
      }
    };

    // Initial load
    updateUserName();

    // Listen for custom family name change events (from ProfilePage)
    const handleFamilyNameChange = () => {
      updateUserName();
    };

    window.addEventListener('familyNameChanged', handleFamilyNameChange);
    
    // Cleanup listener on unmount
    return () => {
      window.removeEventListener('familyNameChanged', handleFamilyNameChange);
    };
  }, []);

  const translations = {
    'en-US': {
      greeting: userName ? `Hello, ${userName}!` : 'Hello!',
      subtitle: 'Ready for a new adventure?',
      title: 'Your Magical Storybook',
      description: 'Every story you create becomes a gift for their dreams.',
      invitation: 'Each night is a chance to share a bit of your heart.\nStart with your first magical adventure — one that\nteaches, soothes, and sparkles.',
      createButton: 'Create your first story'
    },
    'fr-FR': {
      greeting: userName ? `Bonjour, ${userName}!` : 'Bonjour !',
      subtitle: 'Prêt pour une nouvelle aventure ?',
      title: 'Votre Livre d\'Histoires Magiques',
      description: 'Chaque histoire que vous créez devient un cadeau pour leurs rêves.',
      invitation: 'Chaque nuit est une chance de partager un peu de votre cœur.\nCommencez par votre première aventure magique — une qui\nenseigne, apaise et émerveille.',
      createButton: 'Créez votre première histoire'
    }
  };

  const t = translations[language];

  return (
    <div className="h-full bg-gradient-to-br from-purple-100 via-pink-50 to-indigo-100 overflow-y-auto">
      <div className="flex items-center justify-center p-4 pb-32 md:pb-24 min-h-full">
        <div className="max-w-4xl mx-auto text-center">
          {/* Greeting Section - more compact */}
          <div className="mb-4">
            <div className="flex items-center justify-center mb-2">
              <svg className="w-10 h-10 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-800 mb-1">
              {t.greeting}
            </h1>
            <p className="text-base text-gray-600">
              {t.subtitle}
            </p>
          </div>

          {/* Main Content Card - more compact */}
          <div className="bg-white rounded-3xl shadow-2xl p-6 max-w-2xl mx-auto">
            {/* Logo and Title */}
            <div className="mb-4">
              <div className="flex items-center justify-center mb-3">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                  </svg>
                </div>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                <span className="text-lg text-blue-600 mr-2">zzz</span>
                {t.title}
              </h2>
              <p className="text-base text-gray-600 leading-relaxed">
                {t.description}
              </p>
            </div>

            {/* Moon Section - more compact */}
            <div className="my-4">
              <div className="flex justify-center mb-3">
                <div className="text-4xl">🌙</div>
              </div>
              <div className="max-w-xl mx-auto">
                <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
                  {t.invitation}
                </p>
              </div>
            </div>

            {/* Payment Status Message */}
            {showPaymentStatus && paymentMessage && (
              <div className={`mb-4 p-3 rounded-lg text-sm text-center ${
                paymentMessage.includes('successful') || paymentMessage.includes('Payment successful') 
                  ? 'bg-green-50 text-green-700 border border-green-200'
                  : paymentMessage.includes('error') || paymentMessage.includes('failed')
                  ? 'bg-red-50 text-red-700 border border-red-200'
                  : 'bg-blue-50 text-blue-700 border border-blue-200'
              }`}>
                {paymentMessage}
              </div>
            )}

            {/* Create Story Button - Dynamic */}
            <div className="mt-6">
              <button
                onClick={onStartStory}
                className={buttonClasses || "bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white px-8 py-3 rounded-full text-base font-bold transition-all duration-200 transform hover:scale-105 shadow-lg"}
              >
                {buttonText || t.createButton}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomePage;